/* eslint-disable no-undef */
export class NetworkCache {
  /** The name of the cache. */
  private _cacheName: string

  /** The default expiry time (in milliseconds) for this cache. */
  private _defaultExpiry: number

  private _cache: { [key: string]: Response } = [] as any

  /** Keep track of cache names to warn if there are duplicates. */
  private static cacheNames: string[] = []

  /**
   * Class constructor.
   * @param name The name of this cache.
   * @param defaultExpiry (Optional) The default expiry for this cache.
   */
  constructor(name: string, defaultExpiry?: number) {
    // Using this static var instead of caches.has because Caches persist in the browser and we don't want to warn
    // anyone because the cache will already exist on the next app reload.
    if (NetworkCache.cacheNames.includes(name)) {
      console.error(`Cache with name ${name} has already been registered.`)
    }

    NetworkCache.cacheNames.push(name)
    this._cacheName = name
    this._defaultExpiry = defaultExpiry ?? 1000 * 60 * 15
  }

  /**
   * Retrieves data from cache or network. Default cache time is 15 minutes (unless overridden when creating the object.
   * It can also be overridden by request specifying the expiry parameter in the method. Unfortunately the Cache API
   * doesn't support caching POST requests so we couldn't rely on caching the request alone and ended up using the url
   * plus a string as cacheKey.
   * @param req The url of the request.
   * @param options The options (header, etc.) of the request.
   * @param cacheName The name of the cache that is going to be used to append to the req variable. Make sure that you
   * add the necessary information to make it unique, like adding the ids to the name if the request may depend on different
   * ids. (Helps with caching different data from same endpoint.)
   * @param expiry (Optional) The number of milliseconds after which the cache expires. Default is 15 minutes.
   * @returns A promise with the data from the request (either from cache or from network.)
   */
  public async fetchWithCache(
    req: RequestInfo,
    options: RequestInit,
    cacheName: string,
    expiry?: number,
  ): Promise<Response> {
    const cachedResp = this._cache[req + cacheName]
    if (!cachedResp) {
      const response = await fetch(req, options)
      if (response.ok) {
        this._cache[req + cacheName] = response.clone()
      }
      return response
    }

    const date = new Date(cachedResp.headers.get('date'))
    // check if cached file is older than the expiry
    if (Date.now() > date.getTime() + (expiry ?? this._defaultExpiry)) {
      const response = await fetch(req, options)
      if (response.ok) {
        this._cache[req + cacheName] = response.clone()
      }
      return response
    }

    // else return cached version
    return cachedResp.clone()
  }
}
